// src/App.tsx
import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ProductProvider } from "./context/ProductContext";
import { ShopProvider, useShopContext } from "./context/ShopContext";
import { CartProvider } from "./context/CartContext";
import { NotificationProvider } from "./context/NotificationContext";
import { GenericModalProvider } from "./context/GenericModalContext";
import Layout from "./components/common/Layout";
import LoginPage from "./pages/auth/Login";
import RegisterPage from "./pages/auth/Register";
import HomePage from "./pages/home/Home";
import ProductsPage from "./pages/products/ProductsPage";
import ProductDetailPage from "./pages/products/ProductDetailPage";
import CartPage from "./pages/products/Cart";
import OrderDetailPage from "./pages/profile/OrderDetailPage";
import AdminDashboard from "./pages/admin/Dashboard";
import { useEffect } from "react";
import ProfileLayout from "./pages/profile/ProfileLayout";
import OrderHistory from "./pages/profile/OrderHistory";
import UpdateProfile from "./pages/profile/UpdateProfile";
import ChangePassword from "./pages/profile/ChangePassword";
import OrderDetailsPageAdmin from "./pages/admin/order/OrderDetailsPageAdmin";
import TermsAndConditionsPage from "./pages/home/TermsAndConditions";
import PoliciesPageAR from "./pages/home/Polices";
import { WalletCashbackProvider } from "./context/WalletCashbackContext";
import WalletOverviewPage from "./pages/profile/WalletOverviewPage";
import StoreLayout from "./pages/stores/StoreLayout";
import StoreProfilePage from "./pages/stores/StoreProfilePage";
import { StoreSettingsProvider } from "./context/StoreSettingsContext";
import { CompanyWalletProvider } from "./context/CompanyWalletContext";
import WalletAnalyticsPage from "./pages/admin/wallet/WalletAnalyticsPage";
import OrderStatusMonitor from "./pages/admin/order/OrderStatusMonitor";
import { AnalyticsProvider } from "./context/AnalyticsContext";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";

// Import the new legal pages
import PoliciesPageEN from "./pages/home/PoliciesPageEN";
import FAQPage from "./pages/home/FAQPage";
import ContactPage from "./pages/home/ContactPage";
import TermsAndConditionsPageEN from "./pages/home/TermsAndConditionsPageEN";
import NotFoundPage from "./pages/home/NotFoundPage";
import UnauthorizedPage from "./pages/home/UnauthorizedPage";
import ShippingPolicyPage from "./pages/home/ShippingPolicyPage";
import ReturnPolicyPage from "./pages/home/ReturnPolicyPage";

function PrivateRoute({
  children,
}: {
  children: React.ReactElement;
}): React.ReactElement | null {
  const { user, isLoading } = useAuth();
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        Loading...
      </div>
    );
  }
  return user ? children : <Navigate to="/login" replace />;
}

function AdminRoute({
  children,
}: {
  children: React.ReactElement;
}): React.ReactElement | null {
  const { user, isLoading } = useAuth();
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        Loading...
      </div>
    );
  }
  return user?.role === "super_admin" || user?.role === "branch_owner" ? (
    children
  ) : (
    <Navigate to="/unauthorized" replace />
  );
}

function ThemeInitializer() {
  const { selectedStore } = useShopContext();
  useEffect(() => {
    if (selectedStore?.theme) {
      document.documentElement.style.setProperty(
        "--primary",
        selectedStore.theme.primary_color
      );
      document.documentElement.style.setProperty(
        "--primary-dark",
        selectedStore.theme.primary_color
      );
      document.documentElement.style.setProperty(
        "--primary-light",
        selectedStore.theme.primary_color
      );
      document.documentElement.style.setProperty(
        "--secondary",
        selectedStore.theme.secondary_color
      );
      document.documentElement.style.setProperty(
        "--secondary-dark",
        selectedStore.theme.secondary_color
      );
      document.documentElement.style.setProperty(
        "--secondary-light",
        selectedStore.theme.secondary_color
      );
    }
  }, [selectedStore]);
  return null;
}

function App() {
  return (
    <BrowserRouter>
      <AnalyticsProvider>
        <AuthProvider>
          <GenericModalProvider>
            <NotificationProvider>
              <ShopProvider>
                <CartProvider>
                  <ProductProvider>
                    <WalletCashbackProvider>
                      <CompanyWalletProvider>
                        <StoreSettingsProvider>
                          <ThemeInitializer />
                          <OrderStatusMonitor
                            onStatusChange={(orderId, oldStatus, newStatus) => {
                              console.log(
                                `Order ${orderId} changed from ${oldStatus} to ${newStatus}`
                              );
                            }}
                          />

                          <Routes>
                            {/* Auth Routes */}
                            <Route path="/login" element={<Layout><LoginPage /></Layout>} />
                            <Route path="/register" element={<Layout><RegisterPage /></Layout>} />
                            <Route path="/forgot-password" element={<Layout><ForgotPasswordPage /></Layout>} />
                            
                            {/* Legal & Help Pages - AR */}
                            <Route path="/terms-conditions" element={<Layout><TermsAndConditionsPage /></Layout>} />
                            <Route path="/policies" element={<Layout><PoliciesPageAR /></Layout>} />
                            
                            {/* Legal & Help Pages - EN */}
                            <Route path="/terms-conditions-en" element={<Layout><TermsAndConditionsPageEN /></Layout>} />
                            <Route path="/policies-en" element={<Layout><PoliciesPageEN /></Layout>} />
                            <Route path="/faq" element={<Layout><FAQPage /></Layout>} />
                            <Route path="/contact" element={<Layout><ContactPage /></Layout>} />
                            <Route path="/shipping-policy" element={<Layout><ShippingPolicyPage /></Layout>} />
                            <Route path="/refund-policy" element={<Layout><ReturnPolicyPage /></Layout>} />
                            {/* Error Pages */}
                            <Route path="/unauthorized" element={<Layout><UnauthorizedPage /></Layout>} />
                            <Route path="/404" element={<Layout><NotFoundPage /></Layout>} />
                            
                            {/* Main Routes */}
                            <Route path="/" element={<Layout><HomePage /></Layout>} />
                            <Route path="/products" element={<Layout><ProductsPage /></Layout>} />
                            <Route path="/product/:id" element={<Layout><ProductDetailPage /></Layout>} />
                            
                            {/* Protected Routes */}
                            <Route path="/cart" element={<Layout><PrivateRoute><CartPage /></PrivateRoute></Layout>} />
                            
                            {/* Profile Routes */}
                            <Route path="/profile" element={<Layout><PrivateRoute><ProfileLayout /></PrivateRoute></Layout>}>
                              <Route index element={<OrderHistory />} />
                              <Route path="update" element={<UpdateProfile />} />
                              <Route path="change-password" element={<ChangePassword />} />
                              <Route path="wallet" element={<WalletOverviewPage />} />
                            </Route>
                            
                            <Route path="/orders/:id" element={<Layout><PrivateRoute><OrderDetailPage /></PrivateRoute></Layout>} />
                            
                            {/* Store Routes */}
                            <Route path="/store/:slug" element={<StoreLayout><StoreProfilePage /></StoreLayout>} />
                            
                            {/* Admin Routes */}
                            <Route path="/admin" element={<Layout><AdminRoute><AdminDashboard /></AdminRoute></Layout>} />
                            <Route path="/admin/orders/:id" element={<Layout><AdminRoute><OrderDetailsPageAdmin /></AdminRoute></Layout>} />
                            <Route path="/admin/wallet" element={<Layout><AdminRoute><WalletAnalyticsPage /></AdminRoute></Layout>} />
                            
                            {/* Catch all route */}
                            <Route path="*" element={<Navigate to="/404" replace />} />
                          </Routes>
                        </StoreSettingsProvider>
                      </CompanyWalletProvider>
                    </WalletCashbackProvider>
                  </ProductProvider>
                </CartProvider>
              </ShopProvider>
            </NotificationProvider>
          </GenericModalProvider>
        </AuthProvider>
      </AnalyticsProvider>
    </BrowserRouter>
  );
}

export default App;